import React, { useMemo, useState, useCallback } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import type { SingleValue } from 'react-select';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { AppContext } from 'views/App/context';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import {
  ReportingPeriodPicker,
  getTimeFilterOptions,
  getDateFilterValue,
  getDateRangeFromFilter,
  type PeriodOption,
} from '../components/ReportingPeriodPicker';
import { TimeRangeFilter } from 'types/timeRangeFilter';
import MultiSelectPicker from 'components/_pickers/MultiSelectPicker';
import { deriveFields, deriveTemplates } from './_utils/reportDataUtils';
import { useReportData } from './_data_sources/useReportData';
import type { ReportDataRequest } from './_types';

const REPORT_DATA_TIME_RANGE_FILTER = {
  PAST_30_DAYS: TimeRangeFilter.PAST_30_DAYS,
  PREVIOUS_MONTH: TimeRangeFilter.PREVIOUS_MONTH,
  PAST_3_MONTHS: TimeRangeFilter.PAST_3_MONTHS,
  YEAR_TO_DATE: TimeRangeFilter.YEAR_TO_DATE,
  PAST_12_MONTHS: TimeRangeFilter.PAST_12_MONTHS,
  LAST_CALENDAR_YEAR: TimeRangeFilter.LAST_CALENDAR_YEAR,
} as const;

const DEFAULT_TIME_RANGE = TimeRangeFilter.PAST_30_DAYS;

export default function PerformanceReportData() {
  const { currentOrganizationId } = React.useContext(AppContext);
  const [timeRange, setTimeRange] = useState<TimeRangeFilter>(DEFAULT_TIME_RANGE);
  const [selectedFieldIds, setSelectedFieldIds] = useState<number[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
  const reportDataQuery = useReportData(currentOrganizationId);

  const templates = useMemo(
    () => deriveTemplates(reportDataQuery.data ?? []),
    [reportDataQuery.data]
  );

  const fields = useMemo(
    () => deriveFields(reportDataQuery.data ?? []),
    [reportDataQuery.data]
  );

  const fieldOptions = useMemo(
    () => fields.map((f) => ({ value: f.id, label: f.label })),
    [fields]
  );

  const templateOptions = useMemo(
    () => templates.map((t) => ({ value: t.id, label: t.name })),
    [templates]
  );

  const timeRangeOptions = useMemo(
    () =>
      getTimeFilterOptions<typeof REPORT_DATA_TIME_RANGE_FILTER>(
        REPORT_DATA_TIME_RANGE_FILTER,
        'reporting.performanceReportData.timeRange.options'
      ),
    []
  );

  const timeRangeValue = useMemo(
    () =>
      getDateFilterValue<typeof REPORT_DATA_TIME_RANGE_FILTER>(
        timeRange as (typeof REPORT_DATA_TIME_RANGE_FILTER)[keyof typeof REPORT_DATA_TIME_RANGE_FILTER],
        REPORT_DATA_TIME_RANGE_FILTER,
        'reporting.performanceReportData.timeRange.options'
      ),
    [timeRange]
  );

  const handleTimeRangeChange = useCallback(
    (value: SingleValue<PeriodOption<TimeRangeFilter>>) => {
      if (value) setTimeRange(value.value);
    },
    []
  );

  const handleRunReport = useCallback(() => {
    const { startDate, endDate } = getDateRangeFromFilter(timeRange);
    const request: ReportDataRequest = {
      fieldIds: selectedFieldIds,
      ...(selectedTemplateIds.length > 0 && { templateIds: selectedTemplateIds }),
      startDate,
      endDate,
    };
    // TODO: implement report execution
  }, [timeRange, selectedFieldIds, selectedTemplateIds]);

  return (
    <CommonLayout
      headerProps={{
        title: t('reporting.performanceReportData.title'),
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr',
          gap: 4,
          minHeight: 0,
          px: 4,
        }}
      >
        {reportDataQuery.isError && (
          <Alert severity='error' onClose={() => reportDataQuery.refetch()}>
            {t('common.error.loadingData')}
          </Alert>
        )}

        <ReportingPeriodPicker
          value={timeRangeValue}
          options={timeRangeOptions}
          onChange={handleTimeRangeChange}
          nameKey='reporting.performanceReportData.timeRange.name'
          placeholderKey='reporting.performanceReportData.timeRange.placeholder'
        />

        <Paper
          elevation={0}
          sx={{
            p: 4,
            px: 6,
            borderRadius: 2,
            border: `1px solid ${neutral[200]}`,
            backgroundColor: neutral[50],
          }}
        >
          <Stack spacing={3}>
            <MultiSelectPicker
              id='report-data-fields'
              label={t('reporting.performanceReportData.fields.label')}
              placeholder={t('reporting.performanceReportData.fields.placeholder')}
              value={selectedFieldIds}
              onChange={setSelectedFieldIds}
              options={fieldOptions}
              loading={reportDataQuery.isLoading}
            />
            <MultiSelectPicker
              id='report-data-templates'
              label={t('reporting.performanceReportData.templates.label')}
              placeholder={t('reporting.performanceReportData.templates.placeholder')}
              value={selectedTemplateIds}
              onChange={setSelectedTemplateIds}
              options={templateOptions}
              loading={reportDataQuery.isLoading}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant='contained'
                size='medium'
                onClick={handleRunReport}
                disabled={selectedFieldIds.length === 0}
              >
                {t('reporting.performanceReportData.runReport')}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            px: 2,
          }}
        >
          <Box
            component='img'
            src='/admin/img/performance-reporting/empty-report-data.png'
            alt=''
            sx={{ maxWidth: 240, mb: 3 }}
          />
          <Typography
            variant='body1'
            sx={{ textAlign: 'center', maxWidth: 480 }}
          >
            {t('reporting.performanceReportData.emptyState')}
          </Typography>
        </Box>
      </Box>
    </CommonLayout>
  );
}
